import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Send, Link2, FileText, Trash2, ExternalLink, Copy, Check } from "lucide-react";
import Shell, { Breadcrumbs } from "@/components/Shell";
import { Card, Btn, Select, Spinner, StatusBadge, IconBtn } from "@/components/ui-kit";
import TaskItem from "@/components/TaskItem";
import TaskModal from "@/components/TaskModal";
import ProjectProofs from "@/components/ProjectProofs";
import api from "@/lib/api";
import { useConfirm } from "@/lib/confirm";
import { PROJECT_STATUSES, STATUS_BG, formatGBP } from "@/lib/format";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [p, setP] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [linkingQuote, setLinkingQuote] = useState(false);
  const [availableQuotes, setAvailableQuotes] = useState([]);
  const [copied, setCopied] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  const referenceLabel = (() => {
    if (!p) return "";
    let suffix = "";
    if (p.created_at) {
      try {
        const d = new Date(p.created_at);
        if (!isNaN(d)) {
          suffix = " " + d.toLocaleString("en-GB", { month: "long", year: "numeric" });
        }
      } catch {}
    }
    return `${p.job_number} - ${p.client_name} - ${p.name}${suffix}`;
  })();

  const copyReference = async () => {
    if (!referenceLabel) return;
    try {
      await navigator.clipboard.writeText(referenceLabel);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = referenceLabel;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const load = async () => {
    const { data } = await api.get(`/projects/${id}`);
    setP(data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => {});
  }, []);

  const updateStatus = async (status) => {
    await api.patch(`/projects/${id}/status`, { status });
    load();
  };

  const saveTask = async (payload) => {
    if (editingTask) {
      await api.put(`/projects/${id}/tasks/${editingTask.id}`, payload);
    } else {
      await api.post(`/projects/${id}/tasks`, payload);
    }
    setEditingTask(null);
    load();
  };

  const delTask = async (tid) => {
    if (!(await confirm({ title: "Delete this task?", description: "This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/projects/${id}/tasks/${tid}`);
    load();
  };

  const duplicateTask = async (tid) => {
    await api.post(`/projects/${id}/tasks/${tid}/duplicate`);
    load();
  };

  const toggleChosen = async (tid) => {
    await api.post(`/projects/${id}/tasks/${tid}/toggle-chosen`);
    load();
  };

  const chosenSummary = (p?.tasks || []).reduce(
    (acc, t) => {
      if (!t.client_chosen) return acc;
      const v = t.cost_type === "range" ? (t.cost_max || 0) : (t.cost || 0);
      return { count: acc.count + 1, total: acc.total + (parseFloat(v) || 0) };
    },
    { count: 0, total: 0 }
  );

  const changeTaskStatus = async (tid, status) => {
    const task = p.tasks.find((t) => t.id === tid);
    await api.put(`/projects/${id}/tasks/${tid}`, { ...task, status });
    load();
  };

  const openLinkQuote = async () => {
    const { data } = await api.get("/quotes");
    setAvailableQuotes(data.filter((q) => !p.linked_quote_ids.includes(q.id)));
    setLinkingQuote(true);
  };

  const linkQuote = async (quoteId) => {
    await api.post(`/projects/${id}/link-quote/${quoteId}`);
    setLinkingQuote(false);
    load();
  };

  const unlinkQuote = async (quoteId) => {
    await api.post(`/projects/${id}/unlink-quote/${quoteId}`);
    load();
  };

  const [xeroBusy, setXeroBusy] = useState(false);
  const sendToXero = async () => {
    if (!(await confirm({
      title: "Send this project to Xero?",
      description: `A draft invoice will be created with ${chosenSummary.count} line item${chosenSummary.count === 1 ? "" : "s"} (${formatGBP(chosenSummary.total)}). The project will be marked as Invoiced.`,
      confirmText: "Send to Xero",
    }))) return;
    setXeroBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/send-to-xero`);
      alert(`Draft invoice ${data.invoice_number || ""} created in Xero with ${data.line_count} line item${data.line_count === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to send to Xero";
      alert(`Could not send to Xero:\n\n${msg}`);
    } finally {
      setXeroBusy(false);
    }
  };

  if (!p) return <Shell><div className="flex justify-center py-20"><Spinner /></div></Shell>;

  return (
    <Shell>
      <Breadcrumbs items={[{ to: "/projects", label: "Projects" }, { label: p.name }]} />
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-4xl font-bold text-zinc-950">{p.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">Job #{p.job_number}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-zinc-500 mr-1">Status:</span>
          <Select data-testid="project-status-dropdown" value={p.status} onChange={(e) => updateStatus(e.target.value)} className={`!w-auto !py-2 ${STATUS_BG[p.status] || ""}`}>
            {PROJECT_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </Select>
          <Btn variant="secondary" onClick={sendToXero} disabled={xeroBusy} data-testid="send-to-xero-btn"><Send className="w-4 h-4" />{xeroBusy ? "Sending…" : "Send to Xero"}</Btn>
          <Btn variant="secondary" onClick={() => navigate(`/projects/${id}/edit`)} data-testid="edit-project-btn"><Pencil className="w-4 h-4" />Edit Project</Btn>
        </div>
      </div>

      <Card className="p-4 mb-4 flex items-center justify-between gap-3" data-testid="project-reference-card">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest font-semibold mb-1">Folder / Reference</p>
          <p className="text-sm text-zinc-950 truncate" data-testid="project-reference-text">{referenceLabel}</p>
        </div>
        <button
          onClick={copyReference}
          data-testid="project-reference-copy-btn"
          className={`shrink-0 inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs font-medium transition-all ${copied ? "bg-emerald-500 text-white" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}
        >
          {copied ? <><Check className="w-3.5 h-3.5" />Copied</> : <><Copy className="w-3.5 h-3.5" />Copy</>}
        </button>
      </Card>


      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <Card className="p-6">
          <h2 className="font-display text-lg font-semibold text-zinc-950 mb-4">Project Details</h2>
          <p className="text-xs text-zinc-500 uppercase tracking-widest font-semibold mb-1">Client</p>
          <Link to={`/clients/${p.client_id}`} className="text-sm text-zinc-950 hover:text-zinc-700">{p.client_name}</Link>
          {p.total_cost > 0 && (
            <>
              <p className="text-xs text-zinc-500 uppercase tracking-widest font-semibold mt-4 mb-1">Total cost</p>
              <p className="text-sm text-zinc-950 font-semibold">{formatGBP(p.total_cost)}</p>
            </>
          )}
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold text-zinc-950">Linked Quotes</h2>
            <Btn variant="secondary" size="sm" onClick={openLinkQuote} data-testid="link-quote-btn"><Link2 className="w-3.5 h-3.5" />Link Quote</Btn>
          </div>
          {(!p.linked_quotes || p.linked_quotes.length === 0) ? (
            <p className="text-sm text-zinc-500 text-center py-4">No linked quotes</p>
          ) : (
            <div className="space-y-2">
              {p.linked_quotes.map((q) => (
                <div key={q.id} className="flex items-center justify-between p-3 border border-zinc-100 rounded-lg" data-testid={`linked-quote-${q.id}`}>
                  <Link to={`/quotes/${q.id}`} className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-zinc-400" />
                    <span className="font-medium">Q{q.quote_number}</span>
                    <span className="text-zinc-500">— {q.name}</span>
                    <ExternalLink className="w-3 h-3 text-zinc-400" />
                  </Link>
                  <IconBtn variant="destructive" onClick={() => unlinkQuote(q.id)}><Trash2 className="w-4 h-4" /></IconBtn>
                </div>
              ))}
            </div>
          )}
          {linkingQuote && (
            <div className="mt-4 p-3 bg-zinc-50 rounded-lg animate-fade-in">
              <p className="text-xs font-semibold text-zinc-700 mb-2">Select a quote to link:</p>
              {availableQuotes.length === 0 ? (
                <p className="text-xs text-zinc-500">No unlinked quotes available.</p>
              ) : (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {availableQuotes.map((q) => (
                    <button
                      key={q.id}
                      onClick={() => linkQuote(q.id)}
                      className="w-full text-left text-sm p-2 hover:bg-white rounded"
                      data-testid={`link-quote-option-${q.id}`}
                    >
                      Q{q.quote_number} — {q.name}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => setLinkingQuote(false)} className="text-xs text-zinc-500 mt-2">Cancel</button>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-lg font-semibold text-zinc-950">Tasks</h2>
            {chosenSummary.count > 0 && (
              <div
                data-testid="project-chosen-summary"
                className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 text-xs font-medium"
                title="These will be sent to Xero for invoicing"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                {chosenSummary.count} chosen · {formatGBP(chosenSummary.total)}
                <span className="text-[10px] text-emerald-700/70 hidden sm:inline">→ Xero</span>
              </div>
            )}
          </div>
          <Btn size="sm" onClick={() => { setEditingTask(null); setModalOpen(true); }} data-testid="add-task-btn">
            <Plus className="w-3.5 h-3.5" />Add Task
          </Btn>
        </div>
        {(!p.tasks || p.tasks.length === 0) ? (
          <p className="text-sm text-zinc-500 text-center py-8">No tasks yet</p>
        ) : (
          <div className="space-y-2">
            {p.tasks.map((t) => (
              <TaskItem
                key={t.id}
                task={t}
                testIdPrefix="project-task"
                onEdit={(task) => { setEditingTask(task); setModalOpen(true); }}
                onDelete={delTask}
                onDuplicate={duplicateTask}
                onToggleChosen={toggleChosen}
                showDuplicate
                showChosen
                onStatusChange={changeTaskStatus}
              />
            ))}
          </div>
        )}
      </Card>

      <div className="mt-10">
        <ProjectProofs projectId={p.id} projectName={p.name} clientId={p.client_id} clientName={p.client_name} />
      </div>


      <TaskModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingTask(null); }}
        onSave={saveTask}
        task={editingTask}
        title={editingTask ? "Edit Task" : "Add New Task"}
        subtitle={`${editingTask ? "Update" : "Add a task to"} ${p.name}`}
        suppliers={suppliers}
      />
    </Shell>
  );
};

export default ProjectDetail;
